import { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, AlertTriangle, Sparkles } from "lucide-react";
import { api, type JobStatus } from "../api/client";
import JobProgressIndicator from "../components/JobProgressIndicator";

const SOURCES = ["", "NICE", "BNF", "NHS", "PubMed", "MHRA", "SACN"];

export default function LiteraturePage() {
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [jobStatus, setJobStatus] = useState<JobStatus | "idle">("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [answer, setAnswer] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!query.trim() || query.trim().length < 5) {
      setError("Query must be at least 5 characters.");
      return;
    }
    setJobStatus("pending");
    setElapsedMs(0);
    setError(null);
    setAnswer(null);
    setConfidence(null);
    try {
      const res = await api.literatureQuery(
        query,
        source ? { source } : undefined,
        (p) => {
          setJobStatus(p.status);
          setElapsedMs(p.elapsed_ms);
        }
      );
      setAnswer(res.answer);
      setConfidence(res.confidence);
      setJobStatus("completed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setJobStatus("failed");
    }
  };

  const isWorking = jobStatus === "pending" || jobStatus === "running";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BookOpen className="text-nhs-brightblue" /> Literature Q&A
          <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded flex items-center gap-1">
            <Sparkles size={12} /> Opus 4.7
          </span>
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Ask clinical questions — grounded in NICE, BNF, NHS, and UK medical literature.
        </p>
      </div>

      <div className="card">
        <label className="label" htmlFor="query">Your question</label>
        <textarea
          id="query"
          rows={3}
          className="input"
          placeholder="e.g., What is the first-line treatment for type 2 diabetes according to NICE NG28?"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={2000}
          disabled={isWorking}
        />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="label" htmlFor="source">Filter by source (optional)</label>
            <select
              id="source"
              className="input"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              disabled={isWorking}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s || "All sources"}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={submit} disabled={isWorking} className="btn-primary w-full">
              {isWorking ? "Working..." : "Ask"}
            </button>
          </div>
        </div>
      </div>

      <JobProgressIndicator status={jobStatus} elapsedMs={elapsedMs} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {answer && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Answer</h2>
            {confidence !== null && (
              <span
                className={`text-xs px-2 py-1 rounded ${
                  confidence > 0.7
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                Confidence: {(confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800">
            {answer}
          </div>
          <p className="text-xs text-gray-500 italic mt-4 pt-3 border-t">
            AI-generated. Verify against primary sources. Not medical advice.
          </p>
        </motion.div>
      )}
    </div>
  );
}
